import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Link2, X, Trash2 } from "lucide-react";
import * as api from "../../lib/api";
import { Input } from "../ui/input";
import { StatusBadge } from "../shared/Badge";
import { toast } from "sonner";

const LINK_TYPES = {
    related: { label: "Lié à", color: "bg-blue-50 text-blue-700 border-blue-200" },
    duplicate: { label: "Doublon de", color: "bg-amber-50 text-amber-700 border-amber-200" },
    caused_by: { label: "Causé par", color: "bg-purple-50 text-purple-700 border-purple-200" },
};

export { LINK_TYPES };

export default function LinkedTickets({ ticketId, links = [], currentUser, isAgentOrAdmin, onRefresh }) {
    const [linkSearchOpen, setLinkSearchOpen] = useState(false);
    const [linkSearchQuery, setLinkSearchQuery] = useState("");
    const [linkSearchResults, setLinkSearchResults] = useState([]);
    const [linkSearching, setLinkSearching] = useState(false);
    const [linkType, setLinkType] = useState("related");
    const linkSearchTimerRef = useRef(null);

    const handleLinkSearch = useCallback(async (query) => {
        if (!query || query.length < 1) { setLinkSearchResults([]); return; }
        try {
            setLinkSearching(true);
            const results = await api.searchTicketsQuick(query, ticketId);
            setLinkSearchResults(results);
        } catch { setLinkSearchResults([]); }
        finally { setLinkSearching(false); }
    }, [ticketId]);

    useEffect(() => {
        if (linkSearchTimerRef.current) clearTimeout(linkSearchTimerRef.current);
        if (linkSearchQuery.trim()) {
            linkSearchTimerRef.current = setTimeout(() => handleLinkSearch(linkSearchQuery), 300);
        } else {
            setLinkSearchResults([]);
        }
        return () => { if (linkSearchTimerRef.current) clearTimeout(linkSearchTimerRef.current); };
    }, [linkSearchQuery, handleLinkSearch]);

    const handleCreateLink = async (targetTicketId) => {
        try {
            await api.createTicketLink(ticketId, { target_ticket_id: targetTicketId, link_type: linkType }, currentUser.id);
            toast.success("Ticket lié");
            setLinkSearchQuery("");
            setLinkSearchResults([]);
            setLinkSearchOpen(false);
            onRefresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de la liaison");
        }
    };

    const handleDeleteLink = async (linkId) => {
        try {
            await api.deleteTicketLink(ticketId, linkId, currentUser.id);
            toast.success("Lien supprimé");
            onRefresh();
        } catch { toast.error("Erreur lors de la suppression"); }
    };

    if (!isAgentOrAdmin) return null;

    return (
        <div className="card" data-testid="linked-tickets-card">
            <div className="card-header">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-gray-500" />
                        Tickets liés
                    </h3>
                    {!linkSearchOpen && (
                        <button
                            onClick={() => setLinkSearchOpen(true)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            data-testid="add-link-btn"
                        >
                            + Lier
                        </button>
                    )}
                </div>
            </div>
            <div className="card-body space-y-3">
                {linkSearchOpen && (
                    <div className="space-y-2 p-2.5 bg-gray-50 rounded-lg border" data-testid="link-search-panel">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-600">Rechercher un ticket</span>
                            <button
                                onClick={() => { setLinkSearchOpen(false); setLinkSearchQuery(""); setLinkSearchResults([]); }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <Input
                            value={linkSearchQuery}
                            onChange={(e) => setLinkSearchQuery(e.target.value)}
                            placeholder="Numéro ou titre..."
                            className="text-sm h-8"
                            autoFocus
                            data-testid="link-search-input"
                        />
                        <div className="flex gap-1">
                            {Object.entries(LINK_TYPES).map(([key, cfg]) => (
                                <button
                                    key={key}
                                    onClick={() => setLinkType(key)}
                                    className={`px-2 py-0.5 text-[10px] font-medium rounded-full border transition-colors ${linkType === key ? cfg.color : "bg-white text-gray-400 border-gray-200"}`}
                                    data-testid={`link-type-${key}`}
                                >
                                    {cfg.label}
                                </button>
                            ))}
                        </div>
                        {linkSearchResults.length > 0 && (
                            <div className="max-h-40 overflow-y-auto space-y-1" data-testid="link-search-results">
                                {linkSearchResults.map((t) => (
                                    <button
                                        key={t.id}
                                        onClick={() => handleCreateLink(t.id)}
                                        className="w-full flex items-center gap-2 px-2 py-1.5 text-left rounded-md hover:bg-white border border-transparent hover:border-gray-200 transition-colors"
                                        data-testid={`link-result-${t.id}`}
                                    >
                                        <span className="text-[10px] font-mono text-gray-400 flex-shrink-0">{t.ticket_number}</span>
                                        <span className="text-xs text-gray-700 truncate flex-1">{t.title}</span>
                                        <StatusBadge status={t.status} className="!text-[9px] !px-1.5 !py-0" />
                                    </button>
                                ))}
                            </div>
                        )}
                        {linkSearchQuery.trim() && !linkSearching && linkSearchResults.length === 0 && (
                            <p className="text-[10px] text-gray-400 text-center py-1">Aucun résultat</p>
                        )}
                    </div>
                )}

                {links.length > 0 ? (
                    <div className="space-y-1.5" data-testid="linked-tickets-list">
                        {links.map((link) => {
                            const lt = link.linked_ticket;
                            const typeCfg = LINK_TYPES[link.link_type] || LINK_TYPES.related;
                            return (
                                <div key={link.id} className="flex items-center gap-2 p-2 rounded-md border bg-white hover:bg-gray-50 group transition-colors" data-testid={`link-${link.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 mb-0.5">
                                            <span className={`text-[9px] font-medium px-1.5 py-0 rounded-full border ${typeCfg.color}`}>
                                                {typeCfg.label}
                                            </span>
                                            <StatusBadge status={lt.status} className="!text-[9px] !px-1.5 !py-0" />
                                        </div>
                                        <Link
                                            to={`/tickets/${lt.id}`}
                                            className="text-xs font-medium text-gray-900 hover:text-blue-600 truncate block"
                                            data-testid={`link-goto-${lt.id}`}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <span className="font-mono text-gray-400 mr-1">{lt.ticket_number}</span>
                                            {lt.title}
                                        </Link>
                                    </div>
                                    <button
                                        onClick={() => handleDeleteLink(link.id)}
                                        className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                        title="Supprimer le lien"
                                        data-testid={`delete-link-${link.id}`}
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                ) : !linkSearchOpen && (
                    <p className="text-xs text-gray-400 text-center py-2">Aucun ticket lié</p>
                )}
            </div>
        </div>
    );
}
